import { skipToken, useQuery } from '@tanstack/react-query'

import { disableKeyWhenMissing } from '../../helpers/utilities.ts'
import {
  graphql_call as graphql_admin_call,
  graphql_multi_call as graphql_admin_multi_call,
} from '../../schemas/server_admin_query'

import {
  query_admin_fund,
  query_admin_fund_assets,
  query_admin_funds,
} from './fund_graphql'
import { keys } from './keystore.ts'
import type {
  AssetNav,
  AssetWithDailyNav,
  AssetWithNavs,
  FeederWithDailyNavs,
  FeederWithNavs,
  Fund,
  FundDailyNavDetail,
  FundNav,
  SimpleAsset,
} from './types.ts'

export type FundNavsResponse = {
  id: string
  name: string
  starting_price_per_share: number
  navs: {
    nodes: FundNav[]
  }
  feeders: FeederWithNavs[]
}

export type FundDailyNavsResponse = {
  id: string
  name: string
  starting_price_per_share: number
  daily_navs: {
    nodes: FundDailyNavDetail[]
  }
  feeders: FeederWithDailyNavs[]
}

export type FundAssetNavsResponse = {
  assets: {
    nodes: AssetWithNavs[]
    total_count: number
  }
}

export type { AssetWithDailyNav }

export type FundAssetsResponse = {
  assets: {
    nodes: SimpleAsset[]
  }
  fund: {
    assets: {
      nodes: {
        asset: SimpleAsset
        notable: boolean
        id: string
        live_nav: AssetNav | null
        latest_official_nav: AssetNav | null
        latest_published_nav: AssetNav | null
      }[]
    }
  }
}

export type AdminFundsResponse = {
  funds: {
    nodes: Fund[]
    total_count: number
  }
}

export type AdminAssetsResponse = {
  assets: {
    nodes: SimpleAsset[]
  }
}

export function useAdminFund(fundId: string, refetchInterval?: number) {
  return useQuery({
    queryKey: keys.admin_funds.detail(disableKeyWhenMissing(fundId)).queryKey,
    queryFn: !fundId
      ? skipToken
      : async (): Promise<Fund | undefined> => {
          try {
            const response = await graphql_admin_call({
              query: query_admin_fund,
              variables: { id: fundId },
            })
            if (!response) return undefined
            return response as Fund
          } catch (error) {
            console.error('Error fetching fund:', error)
            return undefined
          }
        },
    refetchInterval,
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    enabled: Boolean(fundId),
  })
}

export function useAdminFundAssets(fundId: string) {
  return useQuery({
    queryKey: keys.admin_funds.assets(fundId).queryKey,
    queryFn: async (): Promise<FundAssetsResponse | null> => {
      try {
        if (!fundId) return null
        const response = await graphql_admin_multi_call({
          query: query_admin_fund_assets,
          variables: { fund_id: fundId },
        })
        return response as FundAssetsResponse
      } catch (error) {
        console.error('Error fetching fund companies:', error)
        return null
      }
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    enabled: Boolean(fundId),
  })
}

export function useAdminFunds() {
  return useQuery({
    queryKey: keys.admin_funds.all.queryKey,
    queryFn: async (): Promise<AdminFundsResponse | null> => {
      try {
        const response = await graphql_admin_multi_call({
          query: query_admin_funds,
          variables: {},
        })
        return response as AdminFundsResponse
      } catch (error) {
        console.error('Error fetching all funds:', error)
        return null
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })
}
